import "./CalculatorUnit.scss";
// REACT; ROUTER
import React, { ChangeEvent, useEffect, useState } from "react";

// COMPONENTS & FUNCTIONS
import CalculatorFieldset from "./CalculatorFieldset";
import GraphUnit from "./GraphUnit";
import {
  calculateInputField,
  checkEmptyInputs,
  checkInput,
  clearCalc,
  convertUnit,
  lastInPriorityList,
  populateReceipt,
  updateInputField,
  updatePriorityList,
} from "../../functions/unitHandler";
import { addAnimation } from "../../functions/animationHandler";
import { removeFormatLargeNumbers } from "../../functions/textFormatHandler";
import { updateAppDataFromLocalStorage } from "../../functions/localStorageHandlers";
import { createCalculatorGraphSource } from "../../functions/graphHandler";

// SVG & IMAGES

// INTERFACES
import { CalculatorUnitProps } from "../../interface/propTypesInterface";
import { BaseQuantity } from "../../interface/functionalityInterface";

// NPM INSTALLS
import * as FaIcons from "react-icons/fa";

/*
CALCULATORUNIT COMPONENT INFORMATION
  Calculations made are being updated to local storage as well.
  Concepts of calculator unit:
  - Prioritylist activated when user types in inputs. Last input in prioritylist gets it value calculated.
  - Calculation is being made in equationHandler.ts and used math-expression-evaluator by bugwheels94
  - Calcmode changes figure and inputs of calculator. E.g. change mode of Volume cone to Volume pyramid.
  - When "locking" a field you are essentially placing it last in prioritylist, thus forcing next user input to trigger calculation on the "locked" field.
  - When decreasing decimal amount, the trailing decimals or not removed, When increasing decimal count, they will be visible.
  - meTODO: add drag and drog functionality
*/

function CalculatorUnit(props: CalculatorUnitProps) {
  // ::::: CONSTANTS :::::
  const { removeUnit, duplicateUnit, addToReceipt } = props;
  const listOfCalcmodes = props.inputs.map((calcMode) => calcMode.name);

  // ::::: USEHOOKS :::::

  // entry animation of calculator
  useEffect(() => {
    const calculator = document.getElementById(calc.id);
    setcalcElem(calculator);
    if (!calculator) return;
    calculator.classList.add("show");
  }, []);

  // Change state if props changes
  useEffect(() => {
    setcalc({ ...props });
  }, [props]);

  // ::::: STATES :::::
  const [calc, setcalc] = useState<CalculatorUnitProps>(props);
  const [calcElem, setcalcElem] = useState<HTMLElement | null>();
  const [flipImg, setFlipImg] = useState(false);
  const [showGraph, setShowGraph] = useState(false);

  // ::::: HANDLER FUNCTIONS :::::
  const handleInputs = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const targetValue = removeFormatLargeNumbers(e.target.value).replace(
      ",",
      "."
    );
    // Check if input has prohibited characters
    if (!checkInput(targetValue, calc.enableOperators)) {
      addAnimation(e.target, "shakeHorError");
      return;
    }

    // Update priority list to calculate the last updated input
    const updatedPriorityList = updatePriorityList(calc, e.target.id);
    // Update input field with user input value
    const updatedInput = updateInputField(
      updatedPriorityList,
      e.target.id,
      targetValue
    ) as CalculatorUnitProps;

    // Calculate if all but one input is filled
    if (checkEmptyInputs(updatedInput) <= 1) {
      // Calculate the last edited input field
      const calculatedInput = calculateInputField(updatedInput);
      setcalc(calculatedInput);
      updateAppDataFromLocalStorage(calc.id, calculatedInput);
    } else {
      // If more than two inputs empty do not calculate
      setcalc(updatedInput);
      updateAppDataFromLocalStorage(calc.id, updatedInput);
    }
  };

  const handleTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    // If I want to constrict the title input
    // if (!checkTitle(e.target.value)) {
    //   addAnimation(e.target, "shakeHorError");
    //   return;
    // }
    const calculator = {
      ...calc,
      name: e.target.value,
    };

    setcalc(calculator);
    // Update calculator in local storage
    updateAppDataFromLocalStorage(calc.id, calculator);
  };

  const handleUnits = (
    formInput: BaseQuantity,
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    const index = e.target.options.selectedIndex;
    const unit = e.target.options[index].value;

    const calculator = convertUnit(
      calc,
      formInput,
      unit
    ) as CalculatorUnitProps;

    if (checkEmptyInputs(calculator) <= 1) {
      const calculatedCalc = calculateInputField(calculator);
      setcalc(calculatedCalc);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, calculator);
    } else {
      setcalc(calculator);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, calculator);
    }
  };

  const handleCalcMode = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedIndex = e.target.options.selectedIndex;
    const calcMode = e.target.options[selectedIndex].value;

    const index = calc.inputs.findIndex((mode) => mode.name === calcMode);
    if (index === -1) return;
    const calculator = {
      ...calc,
      selectedCalcMode: index,
      priorityList: calc.inputs[index].baseQuantities.map((bq) => bq.id),
      inputs: calc.inputs.map((input) => {
        return {
          ...input,
          baseQuantities: input.baseQuantities.map((bq) => {
            return {
              ...bq,
              value: "",
            };
          }),
        };
      }),
    };
    setcalc(calculator);
    // Update calculator in local storage
    updateAppDataFromLocalStorage(calc.id, calculator);
  };

  const handleReceipt = () => {
    const receipt = populateReceipt(calc);
    if (receipt) {
      if (calcElem) addAnimation(calcElem, "shakeVertSuccess");
      addToReceipt(receipt);
    } else {
      if (calcElem) addAnimation(calcElem, "shakeHorError");
      addToReceipt(null);
    }
  };

  const handleDecimal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    changeAmount: "increase" | "decrease"
  ) => {
    let decimal = calc.noDecimals;
    if (changeAmount === "increase") {
      // no more than 9 decimals
      if (calc.noDecimals !== 9) {
        decimal++;
      }
    } else {
      // no less then 0 decimals
      if (calc.noDecimals !== 0) {
        decimal--;
      }
    }
    const updatedDecimals: CalculatorUnitProps = {
      ...calc,
      noDecimals: decimal,
    };
    // const calculator = updateDecimals(updatedDecimals);
    if (checkEmptyInputs(updatedDecimals) <= 1) {
      const calculator = calculateInputField(updatedDecimals);
      setcalc(calculator);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, calculator);
    } else {
      setcalc(updatedDecimals);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, updatedDecimals);
    }
  };

  const handleClearCalc = () => {
    const calculator = clearCalc(calc) as CalculatorUnitProps;
    setcalc(calculator);
    // Update calculator in local storage
    updateAppDataFromLocalStorage(calc.id, calculator);

    if (calcElem) addAnimation(calcElem, "shakeVert");
  };

  const handleLock = (targetID: string, isLocked: boolean) => {
    if (isLocked) return;
    const calculator = lastInPriorityList(calc, targetID);
    setcalc(calculator);
  };

  const handleSaveGraph = (
    graphConfiguration: CalculatorUnitProps["graphConfiguration"]
  ) => {
    const calculator = {
      ...calc,
      graphConfiguration,
    };
    setcalc(calculator);
    updateAppDataFromLocalStorage(calc.id, calculator);
  };

  // ::::: JSX :::::
  try {
    return (
      <div id={calc.id} className="calculator">
        <form>
          {/* ---------------------- TOP TOOLBAR ----------------------*/}
          <div className="calculator__buttons">
            <button
              id="calc-delete-button"
              type="button"
              onClick={() => removeUnit(calc.id)}
              className="calculator__delete round-button"
              data-tooltip="delete calc"
            >
              <FaIcons.FaTrash />
            </button>
            <button
              id="calc-clear-button"
              type="button"
              onClick={handleClearCalc}
              className="round-button"
              data-tooltip="clear calc"
            >
              <FaIcons.FaBan />
            </button>
          </div>
          {/* ---------------------- FIGURE ----------------------*/}
          <div
            id="calc-flip-figure"
            className={`calculator__figure ${
              flipImg && calc.inputs[calc.selectedCalcMode].equation
                ? "flip"
                : ""
            }`}
            onClick={() => setFlipImg(!flipImg)}
            style={
              calc.inputs[calc.selectedCalcMode].equation
                ? { cursor: "pointer" }
                : {}
            }
          >
            {calc.inputs[calc.selectedCalcMode].figure ? (
              <img
                id="calc-figure-img"
                src={calc.inputs[calc.selectedCalcMode].figure}
                // alt={calc.name.substring(0, 2)}
                draggable="false"
              />
            ) : (
              <span>{calc.name.substring(0, 5)}</span>
            )}
            {calc.inputs[calc.selectedCalcMode].equation && (
              <code>{calc.inputs[calc.selectedCalcMode].equation}</code>
            )}
          </div>
          {/* ---------------------- TITLE ----------------------*/}
          <fieldset className="calculator__title">
            <label>calculator name</label>
            <input
              id="calc-title-input"
              value={calc.name}
              onChange={handleTitle}
              className={calc.inputs.length <= 1 ? "no-dropdown" : ""}
              style={{
                textAlign: "center",
              }}
              placeholder="calculator name"
            />
          </fieldset>
          {/* ---------------------- CALCULATOR MODE SELECT ----------------------*/}

          {listOfCalcmodes.length > 1 && (
            <div data-tooltip="change calcmode">
              <select
                id="calc-mode-select"
                name={calc.inputs[calc.selectedCalcMode].name.replaceAll(
                  "_",
                  " "
                )}
                defaultValue={calc.inputs[calc.selectedCalcMode].name}
                onChange={(e) => handleCalcMode(e)}
                className="calculator__calcmode"
              >
                {listOfCalcmodes.map((calcmode, index) => {
                  return (
                    <option
                      key={`${calcmode}_${index}`}
                      value={calcmode}
                      id={calcmode}
                    >
                      {calcmode.replaceAll("_", " ")}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          <span className="divider"></span>
          {/* ---------------------- INPUTS ----------------------*/}
          <div className="calculator__inputs">
            {calc.inputs[calc.selectedCalcMode].baseQuantities
              .filter((bq) => bq.type !== "hidden")
              .map((bq) => (
                <CalculatorFieldset
                  bq={bq}
                  key={bq.id}
                  handleInputs={handleInputs}
                  handleUnits={handleUnits}
                  handleLock={handleLock}
                />
              ))}
          </div>
          {/* ---------------------- BOTTOM TOOLBAR ----------------------*/}
          <div className="calculator__toolbar">
            <div style={{ display: "flex" }}>
              <button
                id="calc-receipt-button"
                className="rectangular-button small inverse-color"
                onClick={handleReceipt}
                type="button"
                data-tooltip="save to receipt"
              >
                <FaIcons.FaReceipt />
              </button>
              <button
                id="calc-duplicate-button"
                type="button"
                onClick={() => duplicateUnit(calc)}
                className="rectangular-button small inverse-color"
                data-tooltip="duplicate calc"
              >
                <FaIcons.FaClone />
              </button>
              <button
                id="calc-graph-button"
                type="button"
                onClick={() => setShowGraph(true)}
                className="rectangular-button small inverse-color"
                data-tooltip="show graph"
              >
                <FaIcons.FaChartLine />
              </button>
            </div>
            {/* ---------------------- DECIMALS ----------------------*/}
            <div
              className="calculator__decimal"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <span>no. decimals: {calc.noDecimals}</span>
              <div style={{ display: "flex" }}>
                <div data-tooltip="decrease decimals">
                  <button
                    id="calc-decimal-decrease-button"
                    className="calculator__decimal-button rectangular-button smaller inverse-color"
                    type="button"
                    onClick={(e) => handleDecimal(e, "decrease")}
                  >
                    <FaIcons.FaMinus />
                  </button>
                </div>
                <div data-tooltip="increase decimals">
                  <button
                    id="calc-decimal-increase-button"
                    className="calculator__decimal-button  rectangular-button smaller inverse-color"
                    type="button"
                    onClick={(e) => handleDecimal(e, "increase")}
                  >
                    <FaIcons.FaPlus />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* <span className="calculator__updatedAt">
            updated at: {calc.updatedAt}
          </span> */}
        </form>
        {showGraph && (
          <GraphUnit
            source={createCalculatorGraphSource(calc)}
            savedConfiguration={calc.graphConfiguration}
            saveConfiguration={handleSaveGraph}
            closeGraph={() => setShowGraph(false)}
          />
        )}
      </div>
    );
  } catch (error) {
    return (
      <div
        id={calc.id}
        className="calculator"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "30rem",
        }}
      >
        <h2 style={{ color: "var(--color-error)" }}>Calculator Corrupt</h2>
        <p style={{ textAlign: "center", maxWidth: "30rem" }}>
          Your can either delete the corrupted calculator, or export your data
          and send it to our superuser in the feedback form below
        </p>
        <button
          className="rectangular-button inverse-color"
          style={{ backgroundSize: "100rem" }}
          type="button"
          onClick={() => removeUnit(calc.id)}
        >
          Delete corrupted calculator
        </button>
      </div>
    );
  }
}

export default CalculatorUnit;
